// WO-17 PR-C: Confirmation modal shown when the user returns to the tab
// after viewing a job listing externally (≥ 20 s later).
//
// Mount point: DashboardContainer — always rendered, visibility via `open`.
// Deduplication: DashboardContainer maintains dismissedClickIds so this
// modal fires at most once per clickId.
//
// On 'I applied':
//   Upsert job_applications keyed on (user_id, external_job_id)
//   or (user_id, job_url) — whichever is present first.
//
// Any choice → sessionStorage.removeItem('pendingApplyConfirm').

import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, ExternalLink, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

import { supabase } from '../../../lib/supabase';
import type { Job } from './JobCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ApplyChoice = 'applied' | 'not_yet' | 'declined';

interface ApplyConfirmationModalProps {
  open: boolean;
  /** null only while the modal is hidden (open=false). */
  job: Job | null;
  clickId: string | null;
  onClose: (choice: ApplyChoice) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ApplyConfirmationModal: React.FC<ApplyConfirmationModalProps> = ({
  open,
  job,
  clickId: _clickId,
  onClose,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) {
        sessionStorage.removeItem('pendingApplyConfirm');
        onClose('not_yet');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const handleApplied = async () => {
    if (!job) return;
    setSaving(true);
    setError(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? null;

      if (!userId) {
        // Signed-out user — close without writing; not a blocker.
        sessionStorage.removeItem('pendingApplyConfirm');
        onClose('applied');
        return;
      }

      // ── Find existing row (avoid duplicate tracking) ──
      let existingId: string | null = null;

      if (job.external_job_id) {
        const { data } = await supabase
          .from('job_applications')
          .select('id')
          .eq('user_id', userId)
          .eq('external_job_id', job.external_job_id)
          .maybeSingle();
        existingId = (data as { id: string } | null)?.id ?? null;
      }

      if (!existingId && job.url) {
        const { data } = await supabase
          .from('job_applications')
          .select('id')
          .eq('user_id', userId)
          .eq('job_url', job.url)
          .maybeSingle();
        existingId = (data as { id: string } | null)?.id ?? null;
      }

      const now = new Date().toISOString();

      if (existingId) {
        const { error: upErr } = await supabase
          .from('job_applications')
          .update({ application_status: 'applied', applied_date: now, updated_at: now })
          .eq('id', existingId);
        if (upErr) throw upErr;
      } else {
        const { error: inErr } = await supabase.from('job_applications').insert({
          user_id:         userId,
          job_title:       job.title,
          company_name:    job.company,
          job_url:         job.url,
          application_status: 'applied',
          applied_date:    now,
          source_channel:  job.source,
          external_job_id: job.external_job_id ?? null,
        });
        if (inErr) throw inErr;
      }

      sessionStorage.removeItem('pendingApplyConfirm');
      onClose('applied');
    } catch (e) {
      setError((e as Error).message ?? t('dashboard.jobs.applyConfirm.saveFailed', 'Could not save application.'));
      setSaving(false);
    }
  };

  const close = (choice: ApplyChoice) => {
    sessionStorage.removeItem('pendingApplyConfirm');
    onClose(choice);
  };

  return createPortal(
    <AnimatePresence>
      {open && job && (
        <motion.div
          key="apply-confirm-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={saving ? undefined : () => close('not_yet')}
        >
          <motion.div
            key="apply-confirm-card"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            className="vscode-bg-secondary w-full sm:max-w-sm sm:mx-4 sm:rounded-xl rounded-t-2xl overflow-hidden shadow-xl"
            style={{ paddingBottom: 'max(0px, env(safe-area-inset-bottom))' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ background: 'var(--ggj-header-bg, var(--brand-cta-bg))' }}
            >
              <div className="flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-white/80" />
                <span className="text-sm font-semibold text-white">{t('dashboard.jobs.applyConfirm.title', 'Did you apply?')}</span>
              </div>
              {!saving && (
                <button
                  type="button"
                  onClick={() => close('not_yet')}
                  aria-label={t('layout.close', 'Close')}
                  className="w-7 h-7 flex items-center justify-center rounded text-white/70 hover:text-white hover:bg-white/20 transition-colors touch-manipulation"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              <div className="space-y-0.5">
                <p className="text-sm font-semibold vscode-text-primary line-clamp-2">
                  {job.title}
                </p>
                <p className="text-xs vscode-text-secondary">{job.company}</p>
              </div>

              <p className="text-sm vscode-text-secondary">
                {t('dashboard.jobs.applyConfirm.question', 'Did you submit your application for this role?')}
              </p>

              {error && (
                <p
                  role="alert"
                  className="text-xs text-red-400 bg-red-500/10 rounded-lg px-3 py-2"
                >
                  {error}
                </p>
              )}

              <div className="flex flex-col gap-2">
                {/* Primary: I applied */}
                <button
                  type="button"
                  onClick={handleApplied}
                  disabled={saving}
                  className="min-h-[44px] w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60 touch-manipulation flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                  style={{ background: 'var(--brand-cta-bg)' }}
                >
                  {saving ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0" />
                      {t('dashboard.jobs.applyConfirm.saving', 'Saving…')}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                      {t('dashboard.jobs.applyConfirm.applied', 'I applied')}
                    </>
                  )}
                </button>

                {/* Secondary: Not yet */}
                <button
                  type="button"
                  onClick={() => close('not_yet')}
                  disabled={saving}
                  className="min-h-[44px] w-full px-4 py-2.5 rounded-xl text-sm font-medium vscode-text-primary border vscode-border hover:bg-[var(--vscode-primary)]/5 disabled:opacity-60 touch-manipulation transition-colors"
                >
                  {t('dashboard.jobs.applyConfirm.notYet', 'Not yet')}
                </button>

                {/* Tertiary: Declined */}
                <button
                  type="button"
                  onClick={() => close('declined')}
                  disabled={saving}
                  className="min-h-[44px] w-full px-4 py-2 rounded-xl text-sm vscode-text-tertiary hover:vscode-text-secondary disabled:opacity-60 touch-manipulation transition-colors"
                >
                  {t('dashboard.jobs.applyConfirm.declined', 'Decided not to apply')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default React.memo(ApplyConfirmationModal);
